import {
  buildObjectivesTree,
  addChildObjectivesToNode,
  findAllNodesById,
  buildHierarchicalChildrenMap,
  addNestedChildrenToNode,
  buildChildrenMapRecursively,
} from "src/components/GoalsExplore/utils/objectivesTree.util";
import { updateTreeObject } from "src/shared/TreeView/TreeView.util";

import { GOALS_ACTIONS } from "../actionTypes";

const getRootTeamNode = (rootTeam, treeNodes) => {
  return {
    id: rootTeam.id,
    name: rootTeam.name,
    group: rootTeam,
    groupType: rootTeam.type,
    active: false,
    toggled: true,
    isParentType: true,
    loading: false,
    children: treeNodes.map((node) => ({ ...node, parentNode: null })),
    parentNode: null,
  };
};

const buildRootTreeNodes = (objectives, rootTeam) => {
  const childrenMap = {};

  objectives.forEach((obj) => {
    buildChildrenMapRecursively(objectives, childrenMap, obj.id);
  });

  const objectiveIds = new Set(objectives.map((obj) => obj.id));
  const rootObjectives = objectives.filter(
    (obj) =>
      !obj.parentIds ||
      obj.parentIds.length === 0 ||
      !obj.parentIds.some((pid) => objectiveIds.has(pid))
  );

  const treeNodes = buildObjectivesTree(rootObjectives, childrenMap);

  treeNodes.forEach((node) => {
    addNestedChildrenToNode(node, childrenMap);
  });

  if (rootTeam) {
    return [getRootTeamNode(rootTeam, treeNodes)];
  }

  return treeNodes;
};

const mergeAssociatedTeams = (state, associatedTeams) => {
  const existingTeamIds = new Set(state?.associatedTeams?.map((t) => t.id));
  const newTeams = associatedTeams?.filter((t) => !existingTeamIds.has(t.id));
  return [...(state?.associatedTeams || []), ...(newTeams || [])];
};

const goalsReducer = (state, action) => {
  switch (action.type) {
    case GOALS_ACTIONS.LOAD_ROOT_OBJECTIVES: {
      const objectives = action.payload.objectives || [];
      const rootTeam = action.payload.rootTeam;
      const associatedTeams = action.payload.associatedTeams || [];
      const rawObjectives = [...(objectives || [])];
      const treeData = buildRootTreeNodes(objectives, rootTeam);

      return {
        ...state,
        treeData,
        rawObjectives,
        associatedTeams,
        loading: false,
        error: null,
      };
    }

    case GOALS_ACTIONS.APPEND_ROOT_OBJECTIVES: {
      const objectives = action.payload.objectives || [];
      const appendedAssociatedTeams = action.payload.associatedTeams || [];
      const newNodes = buildRootTreeNodes(objectives);
      const appendedRawObjectives = [
        ...state.rawObjectives,
        ...(objectives || []),
      ];

      const mergedAssociatedTeams = mergeAssociatedTeams(
        state,
        appendedAssociatedTeams
      );

      return {
        ...state,
        treeData: [...state.treeData, ...newNodes],
        rawObjectives: appendedRawObjectives,
        associatedTeams: mergedAssociatedTeams,
        loading: false,
        error: null,
      };
    }

    case GOALS_ACTIONS.LOAD_BRANCH_OBJECTIVES: {
      const {
        objectives = [],
        parentNodeId,
        associatedTeams: branchAssociatedTeams = [],
      } = action.payload;

      const rawObjectives = [...(objectives || [])];

      const mergedAssociatedTeams = mergeAssociatedTeams(
        state,
        branchAssociatedTeams
      );

      const parentNodes = findAllNodesById(state.treeData, parentNodeId);

      // If parent node doesn't exist in tree, add it as a new root node
      if (parentNodes.length === 0) {
        // Find the parent objective in the returned objectives
        const parentObjective = objectives.find(
          (obj) => obj.id === parentNodeId
        );

        if (!parentObjective) {
          // Parent not found in response, return unchanged
          return {
            ...state,
            rawObjectives,
            associatedTeams: mergedAssociatedTeams,
          };
        }

        // Build childrenMap for this branch (parent + its descendants)
        const childrenMap = {};

        buildChildrenMapRecursively(
          objectives,
          childrenMap,
          parentObjective.id,
          true
        );

        // Build tree node for the parent objective
        const newTreeNode = buildObjectivesTree(
          [parentObjective],
          childrenMap
        )[0];

        // Add nested children
        addNestedChildrenToNode(newTreeNode, childrenMap);

        // Add to treeData as a new root node
        return {
          ...state,
          treeData: [...state.treeData, newTreeNode],
          rawObjectives,
          associatedTeams: mergedAssociatedTeams,
          loading: false,
          error: null,
        };
      }

      // Parent node exists, add children to it
      const { directChildren, childrenMap } = buildHierarchicalChildrenMap(
        objectives,
        parentNodeId
      );

      parentNodes.forEach((parentNode) => {
        addChildObjectivesToNode(parentNode, directChildren, childrenMap);

        if (parentNode.children && Array.isArray(parentNode.children)) {
          parentNode.children.forEach((childNode) => {
            if (!childNode.isPlaceholder) {
              addNestedChildrenToNode(childNode, childrenMap);
            }
          });
        }
      });

      return {
        ...state,
        rawObjectives,
        treeData: updateTreeObject(state.treeData),
        associatedTeams: mergedAssociatedTeams,
        loading: false,
        error: null,
      };
    }

    case GOALS_ACTIONS.UPDATE_OBJECTIVE_ATTRIBUTES: {
      const { objectiveId, attributes } = action.payload;

      const nodes = findAllNodesById(state.treeData, objectiveId);

      if (nodes.length === 0) {
        return state;
      }
      nodes.forEach((node) => {
        if (!node.objective) {
          return;
        }
        const existingAttributes = node.objective?.attributes || [];
        const merged = [...existingAttributes];
        attributes?.forEach((updated) => {
          if (updated.id && updated.attributeType) {
            const index = merged.findIndex(
              (a) =>
                a.attributeType === updated.attributeType && a.id === updated.id
            );
            if (index >= 0) {
              merged[index] = { ...merged[index], ...updated };
            } else {
              merged.push(updated);
            }
          }
        });
        // NOTE:: Object assign throws an error when updating the objective attributes

        node.objective = { ...node.objective, attributes: merged };
      });
      return {
        ...state,
        treeData: updateTreeObject(state.treeData),
      };
    }

    case GOALS_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };
    case GOALS_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    default:
      return state;
  }
};

export default goalsReducer;
